// src/mobile_pages/quality/QualityBoard.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Search, Filter, X, Plus, FileText, ClipboardCheck, AlertTriangle, Wrench } from "lucide-react";

import { getQualityMetaList } from "../../api/quality/meta";
import { fetchUserDetail } from "../../api/users/user";

// 문서 작성 메뉴 옵션
const CREATE_OPTIONS = [
  {
    key: "incoming",
    label: "자재검수 (MI)",
    desc: "입고 자재 검수 기록",
    icon: ClipboardCheck,
    path: "incoming/new",
    color: "bg-blue-500",
  },
  {
    key: "process",
    label: "공정검사 (PC)",
    desc: "공정별 품질 검사",
    icon: FileText,
    path: "process/new",
    color: "bg-emerald-500",
  },
  {
    key: "ncr",
    label: "부적합 보고 (NCR)",
    desc: "부적합 사항 기록",
    icon: AlertTriangle,
    path: "ncr/new",
    color: "bg-amber-500",
  },
  {
    key: "car",
    label: "시정조치 (CAR)",
    desc: "시정 및 예방 조치",
    icon: Wrench,
    path: "car/new",
    color: "bg-rose-500",
  },
];

// 게시판 카테고리 라벨
const LABELS = {
  INCOMING: "검측요청서",
  PROCESS: "검측 CheckList",
  NCR: "부적합/시정조치",
};

// 화면에서 토글 가능한 게시판 유형
const ALL_TYPES = ["INCOMING", "PROCESS", "NCR"];

// form_type → 게시판 유형 매핑
const FORM_TYPE_TO_BOARD_TYPE = {
  MI: "INCOMING",
  PC: "PROCESS",
  NC: "NCR",
  CA: "NCR",
};

// 게시판 유형 → 실제 form_type 목록
const BOARD_TYPE_TO_FORM_TYPES = {
  INCOMING: ["MI"],
  PROCESS: ["PC"],
  NCR: ["NC", "CA"],
};

const statusColor = (status) => {
  switch (status) {
    case "submit":
    case "pending":
      return "bg-yellow-100 text-yellow-800";
    case "approved":
      return "bg-green-100 text-green-800";
    case "rejected":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const statusText = (status) =>
  ({
    none: "초안",
    submit: "상신",
    pending: "진행중",
    approved: "승인",
    rejected: "반려",
    cancel: "취소",
  }[status] || status || "-");

// ─────────────── 유틸 ───────────────

const pickTitleFromMeta = (meta) => {
  if (!meta) return "";
  const form = meta.form || {};
  if (typeof form.title === "string" && form.title.trim()) return form.title.trim();

  const om = meta.officialMeta || {};
  const mname = (om.material_name || "").trim();
  const spec = (om.spec || "").trim();
  const dnno = (om.delivery_note_no || "").trim();

  if (mname && spec && dnno) return `[검수] ${mname} (${spec}) - ${dnno}`;
  if (mname && spec) return `[검수] ${mname} (${spec})`;
  if (mname) return `[검수] ${mname}`;

  return meta.__title__ || "품질 문서";
};

const pickDocDateFromMeta = (meta) => {
  if (!meta) return "";
  const form = meta.form || {};
  return (
    form.inspection_datetime ||
    form.doc_datetime ||
    form.datetime ||
    form.inspection_date ||
    form.doc_date ||
    form.date ||
    ""
  );
};

const pickFormTypeFromMeta = (meta) => {
  if (!meta || typeof meta !== "object") return "";
  const clientType = meta.__client__?.form_type;
  const raw = clientType || meta.form_type || meta.doc_type || "";
  return String(raw || "").toUpperCase();
};

const formatName = (u) => {
  if (!u) return "";
  const ln = (u.last_name || "").trim();
  const fn = (u.first_name || "").trim();
  if (ln || fn) return `${ln}${fn}`;
  return (u.name || u.username || "").trim();
};

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("ko-KR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
};

// ─────────────── 컴포넌트 ───────────────

export default function QualityBoard() {
  const { id: projectId, boardType } = useParams();
  const normalizedBoardType = (boardType || "all").toUpperCase();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const [userCache, setUserCache] = useState({});

  // 필터 상태
  const [types, setTypes] = useState(() => {
    if (normalizedBoardType === "ALL") return ALL_TYPES;
    if (ALL_TYPES.includes(normalizedBoardType)) return [normalizedBoardType];
    return ALL_TYPES;
  });
  const [q, setQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  useEffect(() => {
    if (normalizedBoardType === "ALL") setTypes(ALL_TYPES);
    else if (ALL_TYPES.includes(normalizedBoardType)) setTypes([normalizedBoardType]);
    else setTypes(ALL_TYPES);
    setPage(1);
    setRows([]);
  }, [normalizedBoardType]);

  const formTypeFilter = useMemo(() => {
    if (!types || types.length === 0) return [];
    const list = [];
    types.forEach((t) => {
      const mapped = BOARD_TYPE_TO_FORM_TYPES[t];
      if (mapped && mapped.length) list.push(...mapped);
    });
    return Array.from(new Set(list));
  }, [types]);

  const docTypeCsv = useMemo(
    () => (formTypeFilter.length ? formTypeFilter.join(",") : ""),
    [formTypeFilter]
  );

  // 작성자 정보 캐시 로딩
  const ensureUsers = async (ids) => {
    const need = ids.filter((id) => id != null && userCache[String(id)] == null);
    if (!need.length) return;

    try {
      const fetched = await Promise.allSettled(need.map((id) => fetchUserDetail(id)));
      const next = { ...userCache };
      fetched.forEach((res, idx) => {
        const id = need[idx];
        if (res.status === "fulfilled" && res.value) {
          const u = Array.isArray(res.value) || Array.isArray(res.value?.results)
            ? (res.value.results || [])[0]
            : res.value;
          if (u) next[String(id)] = u;
        }
      });
      setUserCache(next);
    } catch (e) {
      console.error("ensureUsers error", e);
    }
  };

  // 목록 로드
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        const params = {
          project: projectId,
          page,
          page_size: pageSize,
          doc_type: docTypeCsv || undefined,
          q: q || undefined,
          ordering: "-created_at",
        };

        const res = await getQualityMetaList(params);
        const data = res || {};
        const items = Array.isArray(data) ? data : data.results || [];

        let mapped = items.map((it) => {
          const meta = it.meta || {};
          const doc = it.doc || {};

          const formType = pickFormTypeFromMeta(meta);
          const boardType = FORM_TYPE_TO_BOARD_TYPE[formType] || "INCOMING";

          const title =
            (doc.title || "").trim() || pickTitleFromMeta(meta) || "(제목 없음)";

          const docDate = pickDocDateFromMeta(meta);

          const createdByObj = doc.created_by || it.created_by || null;
          const createdById =
            typeof createdByObj === "number"
              ? createdByObj
              : createdByObj?.user_id ?? createdByObj?.id ?? null;

          return {
            doc_dns: it.doc_dns || it.dns,
            title,
            created_by_id: createdById,
            created_by_name: formatName(createdByObj) || "",
            created_at: doc.created_at || it.created_at || "",
            status: doc.status || it.status || "none",
            doc_type: boardType,
            raw_form_type: formType,
            doc_date: docDate || "",
          };
        });

        // 사용자 정보 채우기
        const idsToFetch = Array.from(
          new Set(
            mapped
              .filter((m) => m.created_by_id && !m.created_by_name)
              .map((m) => m.created_by_id)
          )
        );
        if (idsToFetch.length) {
          await ensureUsers(idsToFetch);
        }

        mapped = mapped.map((m) => {
          if (!m.created_by_name && m.created_by_id && userCache[String(m.created_by_id)]) {
            const u = userCache[String(m.created_by_id)];
            return { ...m, created_by_name: formatName(u) || "-" };
          }
          return { ...m, created_by_name: m.created_by_name || "-" };
        });

        // 클라이언트 필터 (타입)
        const typesSet = types && types.length ? new Set(types) : new Set(ALL_TYPES);
        mapped = mapped.filter((r) => typesSet.has(r.doc_type));

        setRows(mapped);
        setTotalCount(typeof data.count === "number" ? data.count : mapped.length);
      } catch (e) {
        console.error("[QualityBoard] list load error", e);
      } finally {
        setLoading(false);
      }
    };

    if (projectId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, page, pageSize, docTypeCsv, q, types]);

  // 검색 실행
  const handleSearch = () => {
    setQ(searchInput);
    setPage(1);
    setRows([]);
  };

  // 필터 토글
  const toggleType = (t) => {
    setTypes((prev) =>
      prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]
    );
    setPage(1);
    setRows([]);
  };

  // 행 클릭 → 문서 상세 보기
  const navigateToView = (row) => {
    const formType = row.raw_form_type;
    let context = "ncr";

    if (formType === "MI") context = "incoming";
    else if (formType === "PC") context = "process";
    else if (formType === "NC") context = "ncr";
    else if (formType === "CA") context = "car";

    navigate(
      `/project/${projectId}/quality/${encodeURIComponent(context)}/${encodeURIComponent(row.doc_dns)}/view`
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 본문 영역 */}
      <main className="mx-auto max-w-md px-4 pt-4 pb-8">
        {/* 페이지 타이틀 */}
        <header className="mb-6 pt-2">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-[28px] font-bold tracking-tight text-slate-900">
                품질 게시판
              </h1>
              <p className="mt-1 text-[15px] text-slate-500">
                {normalizedBoardType === "ALL"
                  ? `총 ${totalCount}건의 품질 문서`
                  : `${LABELS[normalizedBoardType] || normalizedBoardType} · 총 ${totalCount}건`}
              </p>
            </div>
            <button
              onClick={() => setShowCreateMenu(true)}
              className="flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-[14px] font-medium text-white shadow-sm active:scale-[0.97] transition-transform"
            >
              <Plus size={18} />
              새 문서
            </button>
          </div>
        </header>

        {/* 검색 & 필터 바 */}
        <section className="mb-4 space-y-3">
          {/* 검색창 */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder="제목, 작성자 검색..."
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm focus:border-gray-400 focus:outline-none"
              />
            </div>
            <button
              onClick={handleSearch}
              className="btn-basic px-4 rounded-xl"
            >
              검색
            </button>
            <button
              onClick={() => setShowFilter(!showFilter)}
              className={`p-2.5 rounded-xl border transition-colors ${
                showFilter ? "bg-gray-900 text-white border-gray-900" : "bg-white border-gray-200"
              }`}
            >
              <Filter size={18} />
            </button>
          </div>

          {/* 필터 패널 */}
          {showFilter && (
            <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between mb-3">
                <span className="text-body font-medium">문서 유형 필터</span>
                <button
                  onClick={() => setShowFilter(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {ALL_TYPES.map((t) => {
                  const active = types.includes(t);
                  return (
                    <button
                      key={t}
                      onClick={() => toggleType(t)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                        active
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {LABELS[t]}
                    </button>
                  );
                })}
                <button
                  onClick={() => {
                    setTypes(ALL_TYPES);
                    setPage(1);
                    setRows([]);
                  }}
                  className="px-3 py-1.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600"
                >
                  전체 선택
                </button>
              </div>
            </div>
          )}

          {/* 현재 필터 표시 */}
          {types.length < ALL_TYPES.length && types.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {types.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs"
                >
                  {LABELS[t]}
                  <button
                    onClick={() => toggleType(t)}
                    className="hover:bg-blue-100 rounded-full p-0.5"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </section>

        {/* 문서 목록 */}
        <section className="space-y-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-[15px] text-slate-400">불러오는 중...</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              <p className="text-[15px] text-slate-400">
                {q ? "검색 결과가 없습니다." : "데이터가 없습니다."}
              </p>
            </div>
          ) : (
            rows.map((row) => (
              <button
                key={row.doc_dns}
                onClick={() => navigateToView(row)}
                className="w-full rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm active:scale-[0.98] transition-transform"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[15px] font-semibold text-slate-900 leading-snug line-clamp-2">
                      {row.title}
                    </p>
                    <p className="mt-1.5 text-[13px] text-slate-500">
                      {row.created_by_name} · {formatDate(row.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-medium">
                      {LABELS[row.doc_type]?.slice(0, 2) || row.doc_type}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${statusColor(row.status)}`}>
                      {statusText(row.status)}
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </section>

        {/* 페이지네이션 */}
        <div className="mt-6 flex items-center justify-center gap-6">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.95] transition"
          >
            <span className="text-[16px]">◀</span>
          </button>
          <span className="text-[15px] font-medium text-slate-700 tabular-nums">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm disabled:opacity-30 disabled:cursor-not-allowed active:scale-[0.95] transition"
          >
            <span className="text-[16px]">▶</span>
          </button>
        </div>
      </main>

      {/* 문서 작성 바텀시트 */}
      {showCreateMenu && (
        <>
          {/* 백드롭 */}
          <div
            className="fixed inset-0 bg-black/40 z-40 transition-opacity"
            onClick={() => setShowCreateMenu(false)}
          />

          {/* 바텀시트 */}
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-3xl shadow-2xl safe-area-bottom animate-slide-up">
            {/* 핸들 바 */}
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 rounded-full bg-slate-300" />
            </div>

            {/* 헤더 */}
            <div className="px-5 pb-3 border-b border-slate-100">
              <h2 className="text-[20px] font-bold text-slate-900">새 문서 작성</h2>
              <p className="mt-0.5 text-[14px] text-slate-500">작성할 문서 유형을 선택하세요</p>
            </div>

            {/* 메뉴 옵션 */}
            <div className="px-4 py-4 space-y-2">
              {CREATE_OPTIONS.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.key}
                    onClick={() => {
                      setShowCreateMenu(false);
                      navigate(`/project/${projectId}/quality/${option.path}`);
                    }}
                    className="w-full flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 active:scale-[0.98] transition-all"
                  >
                    <div className={`w-11 h-11 rounded-xl ${option.color} flex items-center justify-center shadow-sm`}>
                      <Icon size={22} className="text-white" />
                    </div>
                    <div className="flex-1 text-left">
                      <p className="text-[16px] font-semibold text-slate-900">{option.label}</p>
                      <p className="text-[13px] text-slate-500">{option.desc}</p>
                    </div>
                    <span className="text-slate-400">›</span>
                  </button>
                );
              })}
            </div>

            {/* 취소 버튼 */}
            <div className="px-4 pb-4">
              <button
                onClick={() => setShowCreateMenu(false)}
                className="w-full py-3 rounded-2xl bg-slate-200 text-[15px] font-medium text-slate-700 active:scale-[0.98] transition-transform"
              >
                취소
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
